// src/accounts/pages/AccountMarketing.js
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";

export default function AccountMarketing() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [newsletter, setNewsletter] = useState(false);
  const [offers, setOffers] = useState(false);

  const [err, setErr] = useState("");

  // ✅ confirm modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmKey, setConfirmKey] = useState(null); // "newsletter" | "offers"
  const [confirmNextValue, setConfirmNextValue] = useState(false);

  // Load from backend (MySQL)
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const r = await api.get("account/marketing");
        setNewsletter(!!r?.data?.marketing?.newsletter);
        setOffers(!!r?.data?.marketing?.offers);
      } catch (e) {
        setErr("Unable to load marketing preferences.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Persist to MySQL
  const save = async (nextNewsletter, nextOffers) => {
    try {
      setSaving(true);
      setErr("");
      await api.put("account/marketing", {
        newsletter: !!nextNewsletter,
        offers: !!nextOffers,
      });
      return true;
    } catch {
      setErr("Unable to save right now. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  // ✅ Open confirmation modal (do NOT change state yet)
  const requestToggle = (key) => {
    if (saving) return;

    if (key === "newsletter") {
      setConfirmKey("newsletter");
      setConfirmNextValue(!newsletter);
      setConfirmOpen(true);
      return;
    }

    if (key === "offers") {
      setConfirmKey("offers");
      setConfirmNextValue(!offers);
      setConfirmOpen(true);
      return;
    }
  };

  // ✅ Confirm action -> update DB -> update UI
  const confirmYes = async () => {
    if (!confirmKey) return;

    const nextNewsletter = confirmKey === "newsletter" ? confirmNextValue : newsletter;
    const nextOffers = confirmKey === "offers" ? confirmNextValue : offers;

    const ok = await save(nextNewsletter, nextOffers);
    if (ok) {
      setNewsletter(nextNewsletter);
      setOffers(nextOffers);
      closeConfirm();
    }
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmKey(null);
    setConfirmNextValue(false);
  };

  const labelForKey = (key) => {
    if (key === "newsletter") return "Newsletter";
    if (key === "offers") return "Special offers";
    return "Preference";
  };

  const confirmTitle = () => {
    const name = labelForKey(confirmKey);
    return `${confirmNextValue ? "Enable" : "Disable"} ${name}?`;
  };

  const confirmBody = () => {
    if (confirmKey === "newsletter") {
      return confirmNextValue
        ? "You will receive monthly updates and new content emails."
        : "You will stop receiving monthly updates and new content emails.";
    }
    if (confirmKey === "offers") {
      return confirmNextValue
        ? "You will receive promotions, discount codes and special deals."
        : "You will stop receiving promotions, discount codes and special deals.";
    }
    return "Are you sure you want to change this preference?";
  };

  if (loading) {
    return (
      <section className="acctPage">
        <h2 className="acctPageTitle">Marketing</h2>
        <p className="acctMuted">Loading…</p>
      </section>
    );
  }

  return (
    <section className="acctPage">
      <h2 className="acctPageTitle">Marketing</h2>

      <p className="acctMuted" style={{ marginTop: 6 }}>
        Control what you receive from us.
      </p>

      {err ? <div style={errBox}>{err}</div> : null}

      <div style={cardWrap}>
        {/* Newsletter */}
        <div style={row}>
          <div>
            <div style={title}>Newsletter</div>
            <div style={sub}>Monthly updates and new content.</div>
          </div>

          <button
            type="button"
            onClick={() => requestToggle("newsletter")}
            disabled={saving}
            style={pill(newsletter)}
            aria-pressed={newsletter}
            title={newsletter ? "On" : "Off"}
          >
            {newsletter ? "ON" : "OFF"}
          </button>
        </div>

        {/* Offers */}
        <div style={{ ...row, borderBottom: "none" }}>
          <div>
            <div style={title}>Special offers</div>
            <div style={sub}>Promotions, discount codes and deals.</div>
          </div>

          <button
            type="button"
            onClick={() => requestToggle("offers")}
            disabled={saving}
            style={pill(offers)}
            aria-pressed={offers}
            title={offers ? "On" : "Off"}
          >
            {offers ? "ON" : "OFF"}
          </button>
        </div>

        {saving ? (
          <div style={{ marginTop: 10, padding: "0 16px 14px", fontSize: 12, opacity: 0.7 }}>
            Saving…
          </div>
        ) : null}
      </div>

      {/* ✅ Confirmation modal */}
      {confirmOpen && (
        <div style={modalOverlay} role="dialog" aria-modal="true" aria-label="Confirm change">
          <div style={modalCard}>
            <div style={modalTitle}>{confirmTitle()}</div>
            <div style={modalBody}>{confirmBody()}</div>

            <div style={modalActions}>
              <button type="button" onClick={closeConfirm} style={modalBtnSecondary} disabled={saving}>
                Cancel
              </button>

              <button
                type="button"
                onClick={confirmYes}
                style={modalBtnPrimary}
                disabled={saving}
              >
                {saving ? "Saving…" : "Yes, confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const errBox = {
  marginTop: 12,
  padding: 12,
  borderRadius: 12,
  background: "rgba(255,0,0,0.06)",
  border: "1px solid rgba(255,0,0,0.20)",
  fontWeight: 800,
};

const cardWrap = {
  marginTop: 16,
  border: "1px solid rgba(0,0,0,0.12)",
  borderRadius: 16,
  overflow: "hidden",
  background: "#fff",
};

const row = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 14,
  padding: 16,
  borderBottom: "1px solid rgba(0,0,0,0.08)",
};

const title = {
  fontWeight: 900,
  fontSize: 16,
};

const sub = {
  marginTop: 4,
  opacity: 0.8,
};

const pill = (on) => ({
  minWidth: 86,
  padding: "12px 18px",
  borderRadius: 6,
  fontWeight: 600,
  cursor: "pointer",
  border: "1px solid rgba(0,0,0,0.18)",
  background: on ? "#0b5360" : "#f3f3f3",
  color: on ? "#fff" : "#000",
});

/* ===== Modal styles ===== */
const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 18,
  zIndex: 99999,
};

const modalCard = {
  width: "min(320px, 100%)",
  background: "#fff",
  borderRadius: 18,
  border: "1px solid rgba(0,0,0,0.12)",
  boxShadow: "0 18px 48px rgba(0,0,0,0.25)",
  padding: 18,
};

const modalTitle = {
  fontWeight: 900,
  fontSize: 16,
  marginBottom: 8,
};

const modalBody = {
  opacity: 0.85,
  lineHeight: 1.5,
};

const modalActions = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  marginTop: 16,
};

const modalBtnSecondary = {
  padding: "12px 14px",
  borderRadius: 6,
  fontWeight: 600,
  cursor: "pointer",
  border: "1px solid rgba(0,0,0,0.18)",
  background: "rgb(0, 124, 118)",
};

const modalBtnPrimary = {
  padding: "12px 14px",
  borderRadius: 6,
  fontWeight: 60,
  cursor: "pointer",
  border: "1px solid rgba(0,0,0,0.08)",
  background: "#0b5360",
  color: "#fff",
};